import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Eye, FileText, Search, Trash2, Upload, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { documentService } from '../services/documentService';
import { driverService } from '../services/driverService';
import { staticDefinitionService } from '../services/staticDefinitionService';

type PersonnelDriver = {
  id: string;
  userId: string;
  fullName: string;
  phone: string;
  tcNumber: string;
  vehiclePlate: string;
  trailerPlate: string;
  city: string;
  status: string;
  userStatus: string;
  terminationDate: string;
  aliasIds: string[];
};

type EmploymentTab = 'active' | 'terminated';

const DEFAULT_PERSONNEL_DOCUMENT_TYPES = [
  'Kimlik Fotokopisi',
  'Ehliyet',
  'SRC Belgesi',
  'Psikoteknik Belgesi',
  'Sabıka Kaydı',
  'İkametgah',
  'Sağlık Raporu',
  'İşe Giriş Bildirgesi',
  'Sözleşme',
];

const resolveFileUrl = (rawPath: string) => {
  if (!rawPath) return '';
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
};

const normalizeText = (value?: string | null) => String(value || '').toLocaleLowerCase('tr-TR').trim();
const normalizePhone = (value?: string | null) => String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');

const mapDriver = (driver: any): PersonnelDriver => ({
  id: String(driver.id || ''),
  userId: String(driver.user_id || driver.userId || ''),
  fullName: driver.full_name || driver.fullName || driver.name || '-',
  phone: driver.phone || '',
  tcNumber: driver.tc_number || driver.tcNumber || '',
  vehiclePlate: driver.assigned_vehicle_plate || driver.cekici_plaka || '',
  trailerPlate: driver.assigned_trailer_plate || driver.dorse_plaka || '',
  city: driver.city || '',
  status: driver.status || '',
  userStatus: driver.user_status || driver.userStatus || '',
  terminationDate: driver.termination_date_text || driver.termination_date || driver.terminationDate || '',
  aliasIds: [String(driver.id || '')].filter(Boolean),
});

const mergeDrivers = (items: PersonnelDriver[]) => {
  const grouped = new Map<string, PersonnelDriver>();
  for (const item of items) {
    const phoneKey = normalizePhone(item.phone);
    const identityKey = `${normalizeText(item.fullName)}|${phoneKey || item.tcNumber || item.userId || item.id}`;
    const existing = grouped.get(identityKey);
    if (!existing) {
      grouped.set(identityKey, item);
      continue;
    }

    existing.aliasIds = Array.from(new Set([...existing.aliasIds, ...item.aliasIds]));
    if (!existing.tcNumber && item.tcNumber) existing.tcNumber = item.tcNumber;
    if (!existing.vehiclePlate && item.vehiclePlate) existing.vehiclePlate = item.vehiclePlate;
    if (!existing.trailerPlate && item.trailerPlate) existing.trailerPlate = item.trailerPlate;
    if (!existing.city && item.city) existing.city = item.city;
    if (!existing.terminationDate && item.terminationDate) existing.terminationDate = item.terminationDate;
  }
  return Array.from(grouped.values());
};

const isTerminatedDriver = (driver: PersonnelDriver) => {
  if (driver.terminationDate) return true;
  return normalizeText(driver.userStatus) === 'inactive';
};

export function PersonnelFiles() {
  const [searchParams] = useSearchParams();
  const [drivers, setDrivers] = useState<PersonnelDriver[]>([]);
  const [documentsByDriverId, setDocumentsByDriverId] = useState<Record<string, any[]>>({});
  const [documentTypes, setDocumentTypes] = useState<string[]>(DEFAULT_PERSONNEL_DOCUMENT_TYPES);
  const [loading, setLoading] = useState(true);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [employmentTab, setEmploymentTab] = useState<EmploymentTab>('active');
  const [quickUploadTarget, setQuickUploadTarget] = useState<{ driverId: string; documentType: string } | null>(null);
  const [form, setForm] = useState({ documentDate: '', notes: '' });
  const quickUploadInputRef = useRef<HTMLInputElement | null>(null);
  const focusedDriverId = searchParams.get('driverId');

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        const [driverRes, definitionRes] = await Promise.all([
          driverService.getDrivers({ type: 'internal' }),
          staticDefinitionService.getByKey('internal_driver_personnel_document_types').catch(() => ({ data: { items: [] } })),
        ]);
        const rawDrivers = Array.isArray((driverRes as any)?.data) ? (driverRes as any).data : [];
        const nextDrivers = mergeDrivers(rawDrivers.map(mapDriver).filter((driver: PersonnelDriver) => driver.id));
        setDrivers(nextDrivers);
        if (focusedDriverId) setSearchTerm(nextDrivers.find((driver: PersonnelDriver) => driver.id === String(focusedDriverId))?.fullName || '');

        const nextTypes = Array.isArray((definitionRes as any)?.data?.items)
          ? (definitionRes as any).data.items.map((item: any) => String(item.value || item.item_value || '').trim()).filter(Boolean)
          : [];
        setDocumentTypes(nextTypes.length > 0 ? nextTypes : DEFAULT_PERSONNEL_DOCUMENT_TYPES);
      } catch (error) {
        console.error('Personnel files load failed:', error);
        toast.error('Özlük dosyaları sayfası yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    void loadInitial();
  }, [focusedDriverId]);

  useEffect(() => {
    if (drivers.length === 0) {
      setDocumentsByDriverId({});
      return;
    }

    const loadMatrixDocuments = async () => {
      try {
        setMatrixLoading(true);
        const entityIds = drivers.flatMap((driver) => driver.aliasIds).filter(Boolean);
        const response: any = await documentService.getDocuments({ entityType: 'driver_personnel', entityIds: entityIds.join(',') });
        const rows = Array.isArray(response?.data) ? response.data : [];
        const nextMap: Record<string, any[]> = {};
        const aliasToPrimary = new Map<string, string>();
        drivers.forEach((driver) => {
          driver.aliasIds.forEach((aliasId) => aliasToPrimary.set(aliasId, driver.id));
        });
        rows.forEach((doc: any) => {
          const rawEntityId = String(doc.entity_id || doc.entityId || '');
          const entityId = aliasToPrimary.get(rawEntityId) || rawEntityId;
          if (!entityId) return;
          if (!nextMap[entityId]) nextMap[entityId] = [];
          nextMap[entityId].push(doc);
        });
        Object.keys(nextMap).forEach((key) => {
          nextMap[key] = nextMap[key].sort((left, right) => new Date(right.uploaded_at || 0).getTime() - new Date(left.uploaded_at || 0).getTime());
        });
        setDocumentsByDriverId(nextMap);
      } catch (error) {
        console.error('Personnel documents matrix load failed:', error);
        toast.error('Özlük dosyaları alınamadı');
      } finally {
        setMatrixLoading(false);
      }
    };
    void loadMatrixDocuments();
  }, [drivers]);

  const filteredDrivers = useMemo(() => {
    const query = normalizeText(searchTerm);
    return drivers.filter((driver) => {
      const matchesTab = employmentTab === 'terminated' ? isTerminatedDriver(driver) : !isTerminatedDriver(driver);
      if (!matchesTab) return false;
      if (!query) return true;
      return normalizeText(`${driver.fullName} ${driver.phone} ${driver.tcNumber} ${driver.vehiclePlate} ${driver.city}`).includes(query);
    });
  }, [drivers, employmentTab, searchTerm]);

  const employmentCounts = useMemo(() => ({
    active: drivers.filter((driver) => !isTerminatedDriver(driver)).length,
    terminated: drivers.filter(isTerminatedDriver).length,
  }), [drivers]);

  const matrixRows = useMemo(() => filteredDrivers.map((driver) => {
    const docs = documentsByDriverId[driver.id] || [];
    const byType = new Map<string, any[]>();
    docs.forEach((doc) => {
      const key = String(doc.document_type || doc.documentType || '').trim() || 'other';
      const current = byType.get(key) || [];
      current.push(doc);
      byType.set(key, current);
    });
    return { driver, byType };
  }), [documentsByDriverId, filteredDrivers]);

  const visibleMatrixRows = useMemo(() => {
    if (!showOnlyMissing) return matrixRows;
    return matrixRows.filter(({ byType }) => documentTypes.some((type) => (byType.get(type) || []).length === 0));
  }, [documentTypes, matrixRows, showOnlyMissing]);

  const totalPages = Math.max(1, Math.ceil(visibleMatrixRows.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const paginatedRows = visibleMatrixRows.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [employmentTab, searchTerm, showOnlyMissing, pageSize]);

  const matrixSummary = useMemo(() => {
    const totalSlots = matrixRows.length * documentTypes.length;
    let missingCount = 0;
    matrixRows.forEach(({ byType }) => {
      documentTypes.forEach((type) => {
        if ((byType.get(type) || []).length === 0) missingCount += 1;
      });
    });
    return {
      driverCount: matrixRows.length,
      totalSlots,
      missingCount,
      completedCount: Math.max(0, totalSlots - missingCount),
    };
  }, [documentTypes, matrixRows]);

  const openDocument = (doc: any) => {
    const url = resolveFileUrl(doc.file_path || doc.file_url || '');
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const refreshDriverDocuments = async (driverId: string) => {
    const driver = drivers.find((item) => item.id === driverId);
    const entityIds = driver?.aliasIds || [driverId];
    const refreshed: any = await documentService.getDocuments({ entityType: 'driver_personnel', entityIds: entityIds.join(',') });
    const rows = Array.isArray(refreshed?.data) ? refreshed.data : [];
    setDocumentsByDriverId((current) => ({ ...current, [driverId]: rows.sort((left: any, right: any) => new Date(right.uploaded_at || 0).getTime() - new Date(left.uploaded_at || 0).getTime()) }));
  };

  const handleQuickUploadClick = (driverId: string, documentType: string) => {
    setQuickUploadTarget({ driverId, documentType });
    quickUploadInputRef.current?.click();
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !quickUploadTarget) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    const invalidFile = files.find((file) => !allowedTypes.includes(file.type) && !/\.(pdf|jpe?g|png|webp)$/i.test(file.name));
    if (invalidFile) {
      toast.error('Sadece PDF, JPG, PNG veya WEBP yüklenebilir');
      event.target.value = '';
      return;
    }
    try {
      setUploading(true);
      for (const file of files) {
        const payload = new FormData();
        payload.append('file', file);
        payload.append('entityType', 'driver_personnel');
        payload.append('entityId', quickUploadTarget.driverId);
        payload.append('documentType', quickUploadTarget.documentType);
        payload.append('name', file.name);
        if (form.documentDate) payload.append('documentDate', form.documentDate);
        if (form.notes) payload.append('notes', form.notes);
        await documentService.uploadDocument(payload);
      }
      await refreshDriverDocuments(quickUploadTarget.driverId);
      setForm({ documentDate: '', notes: '' });
      toast.success(files.length > 1 ? `${files.length} özlük evrağı yüklendi` : 'Özlük evrağı yüklendi');
    } catch (error) {
      console.error('Personnel document upload failed:', error);
      toast.error('Özlük evrağı yüklenemedi');
    } finally {
      setUploading(false);
      setQuickUploadTarget(null);
      event.target.value = '';
    }
  };

  const handleDelete = async (driverId: string, documentId: string | number) => {
    try {
      await documentService.deleteDocument(documentId);
      setDocumentsByDriverId((current) => ({ ...current, [driverId]: (current[driverId] || []).filter((item) => String(item.id) !== String(documentId)) }));
      toast.success('Özlük evrağı silindi');
    } catch (error) {
      console.error('Personnel document delete failed:', error);
      toast.error('Özlük evrağı silinemedi');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Özlük Dosyaları</h1>
          <p className="mt-1 text-gray-500">Özmal sürücüler için özlük evraklarını matris görünümünde yönetin</p>
        </div>
        <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-end">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Evrak Tarihi</label>
            <input type="date" value={form.documentDate} onChange={(e) => setForm((current) => ({ ...current, documentDate: e.target.value }))} className="h-10 rounded-xl border border-slate-300 px-3 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Not</label>
            <input value={form.notes} onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))} placeholder="Yükleme notu" className="h-10 rounded-xl border border-slate-300 px-3 text-sm" />
          </div>
        </div>
      </div>

      <input ref={quickUploadInputRef} type="file" multiple accept="application/pdf,.pdf,image/jpeg,image/png,image/jpg,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} />

      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-4">
        <div><div className="text-xs uppercase tracking-wide text-slate-500">Sürücü Sayısı</div><div className="mt-1 text-2xl font-bold text-slate-900">{matrixSummary.driverCount}</div></div>
        <div><div className="text-xs uppercase tracking-wide text-slate-500">Toplam Belge Slotu</div><div className="mt-1 text-2xl font-bold text-slate-900">{matrixSummary.totalSlots}</div></div>
        <div><div className="text-xs uppercase tracking-wide text-slate-500">Yüklü</div><div className="mt-1 text-2xl font-bold text-emerald-700">{matrixSummary.completedCount}</div></div>
        <div><div className="text-xs uppercase tracking-wide text-slate-500">Eksik</div><div className="mt-1 text-2xl font-bold text-rose-700">{matrixSummary.missingCount}</div></div>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setEmploymentTab('active')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition ${employmentTab === 'active' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Çalışan ({employmentCounts.active})
          </button>
          <button
            type="button"
            onClick={() => setEmploymentTab('terminated')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition ${employmentTab === 'terminated' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            İşten Ayrılan ({employmentCounts.terminated})
          </button>
        </div>
        <div className="relative max-w-xl flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Sürücü, telefon, TC, plaka veya şehir ara" className="h-11 w-full rounded-xl border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" checked={showOnlyMissing} onChange={(e) => setShowOnlyMissing(e.target.checked)} className="rounded border-slate-300" />
          Sadece eksikleri göster
        </label>
      </div>

      <div className="overflow-hidden rounded-2xl border border-black bg-white shadow-sm">
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 top-0 z-40 min-w-[260px] border-b border-r border-black bg-gray-50 px-4 py-3 text-left font-medium text-gray-600">Sürücü</th>
                {documentTypes.map((type) => (
                  <th key={type} className="sticky top-0 z-20 min-w-[210px] border-b border-r border-black bg-gray-50 px-4 py-3 text-left font-medium text-gray-600">{type}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading || matrixLoading ? (
                <tr><td colSpan={documentTypes.length + 1} className="px-4 py-10 text-center text-gray-500">Yükleniyor...</td></tr>
              ) : visibleMatrixRows.length === 0 ? (
                <tr><td colSpan={documentTypes.length + 1} className="px-4 py-10 text-center text-gray-500">Kayıt bulunamadı</td></tr>
              ) : paginatedRows.map(({ driver, byType }) => (
                <tr key={`personnel-${driver.id}`} className="border-t border-black">
                  <td className="sticky left-0 z-[1] border-r border-black bg-white px-4 py-3 align-top">
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-violet-100 p-2 text-violet-700"><Users className="h-4 w-4" /></div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900">{driver.fullName}</div>
                        <div className="mt-1 text-xs text-gray-500">{driver.phone || '-'} {driver.tcNumber ? `• ${driver.tcNumber}` : ''}</div>
                        <div className="mt-1 text-xs text-gray-400">{driver.vehiclePlate || '-'} {driver.trailerPlate ? `/ ${driver.trailerPlate}` : ''}</div>
                        {driver.aliasIds.length > 1 ? <div className="mt-1 text-[11px] font-semibold text-amber-700">Ayni kisiye ait yinelenen kayitlar birlestirildi ({driver.aliasIds.length})</div> : null}
                        {isTerminatedDriver(driver) ? <div className="mt-2 inline-flex rounded-full bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-700">İşten ayrıldı{driver.terminationDate ? ` • ${driver.terminationDate}` : ''}</div> : null}
                      </div>
                    </div>
                  </td>
                  {documentTypes.map((type) => {
                    const docs = byType.get(type) || [];
                    const latest = docs[0] || null;
                    const previewUrl = latest ? resolveFileUrl(latest.file_path || latest.file_url || '') : '';
                    const isImage = String(latest?.mime_type || '').startsWith('image/');
                    return (
                      <td key={`${driver.id}-${type}`} className={`border-r border-black px-4 py-3 align-top ${latest ? 'bg-white' : 'bg-rose-50/70'}`}>
                        {latest ? (
                          <div className="space-y-2">
                            {isImage && previewUrl ? (
                              <button type="button" onClick={() => openDocument(latest)} className="block overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                                <img src={previewUrl} alt={latest.file_name || 'Belge'} className="h-20 w-full object-cover" />
                              </button>
                            ) : (
                              <div className="flex h-20 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-slate-400"><FileText className="h-7 w-7" /></div>
                            )}
                            <div className="text-xs font-medium text-gray-700">{docs.length} belge</div>
                            <div className="space-y-1">
                              {docs.map((doc) => (
                                <div key={doc.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                                  <div className="truncate text-xs font-medium text-gray-700" title={doc.file_name || ''}>{doc.file_name || 'Belge'}</div>
                                  <div className="mt-1 text-[11px] text-slate-500">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('tr-TR') : ''}</div>
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    <button type="button" onClick={() => openDocument(doc)} className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100">
                                      <Eye className="h-3 w-3" /> Önizle
                                    </button>
                                    <button type="button" onClick={() => void handleDelete(driver.id, doc.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-100">
                                      <Trash2 className="h-3 w-3" /> Sil
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button type="button" onClick={() => handleQuickUploadClick(driver.id, type)} disabled={uploading} className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                                <Upload className="h-3.5 w-3.5" /> Dosya Ekle
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-xs font-semibold text-rose-700">Eksik</div>
                            <div className="text-xs text-rose-500">Bu özlük evrağı yüklenmemiş.</div>
                            <button type="button" onClick={() => handleQuickUploadClick(driver.id, type)} disabled={uploading} className="inline-flex items-center gap-1 rounded-lg bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-200 disabled:opacity-60">
                              <Upload className="h-3.5 w-3.5" /> {uploading && quickUploadTarget?.driverId === driver.id && quickUploadTarget?.documentType === type ? 'Yükleniyor...' : 'Yükle'}
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-black bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-600">
            {visibleMatrixRows.length === 0
              ? 'Kayıt yok'
              : `${(clampedPage - 1) * pageSize + 1}-${Math.min(clampedPage * pageSize, visibleMatrixRows.length)} / ${visibleMatrixRows.length} kayıt`}
          </div>
          <div className="flex items-center gap-3">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>{size} / sayfa</option>
              ))}
            </select>
            <button type="button" onClick={() => setPage(clampedPage - 1)} disabled={clampedPage <= 1} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" /> Önceki
            </button>
            <span className="text-sm font-medium text-slate-700">Sayfa {clampedPage} / {totalPages}</span>
            <button type="button" onClick={() => setPage(clampedPage + 1)} disabled={clampedPage >= totalPages} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
              Sonraki <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500">Evrak türleri Ayarlar &gt; Statik Liste Tanımları altında <code>internal_driver_personnel_document_types</code> anahtarından yönetilir.</p>
    </div>
  );
}
